<template>
  <div class="search-screen">
    <h4 class="cnpj-section-title">PESQUISA CNPJ</h4>
    <p class="cnpj-section-subtitle">
      Insira aqui as informações para busca do CNPJ
    </p>

    <!-- 🔍 CAMPOS DE PESQUISA -->
    <div class="search-fields">
      <div class="input-group" v-for="field in fields" :key="field.name">
        <!-- 🏷️ Label padrão -->
        <template v-if="field.name !== 'cnaesText'">
          <label :for="field.name">{{ field.label }}</label>
        </template>

        <!-- 🏷️ Label + tooltip para CNAE(s) -->
        <template v-else>
          <div class="label-with-tooltip">
            <label :for="field.name">{{ field.label }}</label>

            <div class="cnae-tooltip">
              <span class="cnae-help-icon">?</span>
              <div class="cnae-tooltip-content">
                <p><strong>10 principais CNAEs utilizados pela Magnum</strong></p>
                <ul>
                  <li>4530703 – Comércio a varejo de peças e acessórios novos para veículos automotores</li>
                  <li>4530705 – Comércio a varejo de pneumáticos e câmaras-de-ar</li>
                  <li>4930202 – Transporte rodoviário de carga, exceto produtos perigosos e mudanças, intermunicipal,
                    interestadual</li>
                  <li>4520001 – Serviços de manutenção e reparação mecânica de veículos automotores</li>
                  <li>4520006 – Serviços de borracharia para veículos automotores</li>
                  <li>4930201 – Transporte rodoviário de carga, exceto produtos perigosos e mudanças, municipal</li>
                  <li>4744099 – Comércio varejista de materiais de construção em geral</li>
                  <li>4530702 – Comércio por atacado de pneumáticos e câmaras-de-ar</li>
                  <li>4520004 – Serviços de alinhamento e balanceamento de veículos automotores</li>
                  <li>4120400 – Construção de edifícios</li>
                </ul>
              </div>
            </div>
          </div>
        </template>

        <!-- SELECT -->
        <select v-if="field.type === 'select'" v-model="searchData[field.name]">
          <option v-for="option in field.options" :key="option.value" :value="option.value">
            {{ option.text }}
          </option>
        </select>

        <!-- MONEY -->
        <input v-else-if="field.type === 'money'" :placeholder="field.placeholder" :value="searchData[field.name]"
          @focus="onMoneyFocus(field.name)" @input="onMoneyInput(field.name, $event)" @blur="onMoneyBlur(field.name)"
          @keyup.enter="searchCNPJ" />

        <!-- OUTROS -->
        <input v-else v-model="searchData[field.name]" :type="field.type" :placeholder="field.placeholder"
          @keyup.enter="searchCNPJ" />
      </div>
    </div>

    <button class="search-button" @click="searchCNPJ">
      <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"
        class="menu-item-icon">
        <path
          d="M19.5293 17.1318C19.8773 17.5186 19.8773 18.0986 19.4906 18.4467L18.4078 19.5295C18.0598 19.9162 17.4797 19.9162 17.093 19.5295L13.2645 15.701C13.0711 15.5076 12.9937 15.2756 12.9937 15.0436V14.3861C11.6016 15.4689 9.9 16.0877 8.04375 16.0877C3.59648 16.0877 -9.53674e-07 12.4912 -9.53674e-07 8.04394C-9.53674e-07 3.63535 3.59648 0.000194311 8.04375 0.000194311C12.4523 0.000194311 16.0875 3.63535 16.0875 8.04394C16.0875 9.93887 15.4301 11.6404 14.3859 12.9939H15.0047C15.2367 12.9939 15.4687 13.11 15.6621 13.2646L19.5293 17.1318ZM8.04375 12.9939C10.7508 12.9939 12.9937 10.7896 12.9937 8.04394C12.9937 5.33691 10.7508 3.09394 8.04375 3.09394C5.29805 3.09394 3.09375 5.33691 3.09375 8.04394C3.09375 10.7896 5.29805 12.9939 8.04375 12.9939Z"
          fill="white" />
      </svg>
      Pesquisar
    </button>

    <!-- 🧾 RESULTADOS -->
    <div class="table-wrapper">
      <div class="tableContainer">
        <table v-if="paginatedResults.length" class="tableClient">
          <thead>
            <tr>
              <th>RAZÃO SOCIAL</th>
              <th>CNPJ</th>
              <th>CAPITAL SOCIAL</th>
              <th>SITUAÇÃO</th>
              <th>TELEFONE1</th>
              <th>TELEFONE2</th>
              <th v-if="isWhatsConnected">WHATSAPP</th>
              <th>EMAIL</th>
              <th>UF</th>
              <th>MUNICÍPIO</th>
              <th>CEP</th>
              <th>BAIRRO</th>
              <th>ENDEREÇO COMPLETO</th>
              <th>NATUREZA JURÍDICA</th>
              <th>PORTE</th>
              <th>DATA INÍCIO ATIVIDADE</th>
              <th>CNAE FISCAL</th>
              <th>DESCRIÇÃO CNAE</th>
              <th>SÓCIO PRINCIPAL</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(empresa, index) in paginatedResults" :key="index">
              <td>{{ empresa.RAZAO_SOCIAL }}</td>
              <td>{{ formatCNPJ(empresa.COD_CNPJ) }}</td>
              <td>{{ formatMoneyBR(empresa.CAPITAL_SOCIAL_EMPRESA) }}</td>
              <td>{{ empresa.SITUACAO_CADASTRAL }}</td>
              <td>{{ formatPhoneBR(empresa.TELEFONE_1) }}</td>
              <td>{{ formatPhoneBR(empresa.TELEFONE_2) }}</td>
              <td v-if="isWhatsConnected">
                <span v-if="empresa.hasWhatsApp === 'valid'">✅</span>
                <span v-else-if="empresa.hasWhatsApp === 'invalid'">❌</span>
                <span v-else-if="empresa.hasWhatsApp === 'unavailable'">📴</span>
              </td>
              <td>{{ (empresa.CORREIO_ELETRONICO || "").toLowerCase() }}</td>
              <td>{{ empresa.UF }}</td>
              <td>{{ empresa.Nome_Municipio }}</td>
              <td>{{ formatCEP(empresa.CEP) }}</td>
              <td>{{ empresa.BAIRRO }}</td>
              <td>{{ empresa.ENDERECO }}</td>
              <td>{{ empresa.NATUREZA_JURIDICA }}</td>
              <td>{{ formatarPorte(empresa.PORTE_EMPRESA) }}</td>
              <td>{{ formatDate(empresa.DATA_INICIO_ATIVIDADE) }}</td>
              <td>{{ empresa.CNAE_FISCAL }}</td>
              <td>{{ empresa.DESCRICAO_CNAE }}</td>
              <td>{{ empresa.NOME_RAZAO_SOCIAL_SOCIO }}</td>
            </tr>
          </tbody>
        </table>

        <div v-if="loading" class="loading-container">
          <div class="loading-bar"></div>
          <p>Carregando resultados...</p>
        </div>

        <p v-else-if="!paginatedResults.length && !loading" style="margin-top: 20px">
          Sem resultados para a pesquisa.
        </p>
      </div>

      <!-- 🔹 PAGINAÇÃO -->
      <div class="pagination-container">
        <p class="record-count">Total de registros: {{ cnpjResults.length }}</p>

        <div class="pagination">
          <span class="page-label">Páginas:</span>

          <button class="pagination-button" @click="previousPage" :disabled="currentPage === 1">
            «
          </button>

          <span class="page-current">{{ currentPage }}</span>

          <button class="pagination-button" @click="nextPage" :disabled="currentPage === totalPages">
            »
          </button>

          <span class="page-info"> {{ currentPage }} de {{ totalPages }} </span>
        </div>
      </div>

      <!-- 🔹 AÇÕES -->
      <div class="export-container">
        <button class="small-button" @click="openCSVExport" :disabled="loading || exportingCSV || !cnpjResults.length">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M3.9974 9.07464H9.07073V4.0013H15.9974V9.58797H17.3307V4.0013C17.3307 3.64768 17.1903 3.30854 16.9402 3.05849C16.6902 2.80844 16.351 2.66797 15.9974 2.66797H7.24406L2.66406 7.24797V20.0013C2.66406 20.3549 2.80454 20.6941 3.05459 20.9441C3.30464 21.1942 3.64377 21.3346 3.9974 21.3346H15.9974C16.351 21.3346 16.6902 21.1942 16.9402 20.9441C17.1903 20.6941 17.3307 20.3549 17.3307 20.0013H3.9974V9.07464ZM3.9974 7.79463L7.79073 4.0013H7.9974V8.0013H3.9974V7.79463Z"
              fill="#4D5163" />
            <path
              d="M18.8826 10.8986C18.7551 10.7894 18.591 10.7323 18.4232 10.7388C18.2555 10.7453 18.0963 10.8148 17.9776 10.9335C17.8588 11.0523 17.7893 11.2114 17.7828 11.3792C17.7763 11.547 17.8334 11.7111 17.9426 11.8386L20.1093 13.9986H12.0026C11.8258 13.9986 11.6562 14.0688 11.5312 14.1938C11.4062 14.3189 11.3359 14.4884 11.3359 14.6653C11.3359 14.8421 11.4062 15.0116 11.5312 15.1367C11.6562 15.2617 11.8258 15.3319 12.0026 15.3319H20.1293L17.9426 17.5186C17.8728 17.5784 17.8161 17.6519 17.7761 17.7346C17.7361 17.8173 17.7136 17.9074 17.7101 17.9992C17.7065 18.091 17.722 18.1826 17.7555 18.2681C17.789 18.3537 17.8399 18.4314 17.9048 18.4964C17.9698 18.5613 18.0475 18.6122 18.1331 18.6457C18.2186 18.6792 18.3102 18.6947 18.402 18.6911C18.4938 18.6876 18.5839 18.6651 18.6666 18.6251C18.7493 18.5851 18.8228 18.5284 18.8826 18.4586L22.6693 14.6653L18.8826 10.8986Z"
              fill="#4D5163" />
          </svg>
          Exportar CSV
        </button>
        <button class="small-button" @click="prepareCNPJForSending">
          <svg width="31" height="31" viewBox="0 0 31 31" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd"
              d="M25.0676 5.31937C22.447 2.69721 18.962 1.25159 15.2492 1.25C7.59605 1.25 1.36941 7.47346 1.36781 15.1234C1.36463 17.5577 2.00306 19.9506 3.21941 22.0601L1.25 29.25L8.60862 27.3204C10.6449 28.4285 12.9248 29.0096 15.2428 29.0096H15.2492C22.8992 29.0096 29.1258 22.7845 29.129 15.1346C29.1306 11.4282 27.6882 7.94313 25.0676 5.31937Z"
              stroke="#4D5163" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
            <path fill-rule="evenodd" clip-rule="evenodd"
              d="M13.6594 16.1943C12.7912 15.2296 12.1367 14.1495 11.7018 13.0538C11.6098 12.8221 11.6639 12.5508 11.825 12.3719L12.4328 11.6975C12.9307 11.1442 12.9307 10.3618 12.4958 9.87864L11.6246 8.91068C11.045 8.26675 10.1056 8.26675 9.52599 8.91068L9.04215 9.44825C8.49226 10.0592 8.26295 10.9406 8.41137 11.8146C8.77796 13.969 9.90445 16.3278 11.7218 18.347C13.5392 20.3662 15.6623 21.6178 17.6014 22.0251C18.388 22.19 19.1813 21.9352 19.7312 21.3243L20.2143 20.7875C20.7939 20.1436 20.7939 19.0998 20.2143 18.4559L19.3438 17.4887C18.909 17.0056 18.204 17.0056 17.7699 17.4887L17.0998 18.2341C16.9387 18.413 16.6946 18.4732 16.486 18.3709C15.4998 17.887 14.5277 17.1589 13.6594 16.1943Z"
              fill="#4D5163" />
          </svg>
          Enviar Mensagem
        </button>
        <button class="small-button" @click="toggleMap">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd"
              d="M16.272 10.2725C16.272 11.3333 15.8505 12.3507 15.1004 13.1009C14.3503 13.851 13.3328 14.2725 12.272 14.2725C11.2111 14.2725 10.1937 13.851 9.44355 13.1009C8.6934 12.3507 8.27197 11.3333 8.27197 10.2725C8.27197 9.2116 8.6934 8.19418 9.44355 7.44403C10.1937 6.69389 11.2111 6.27246 12.272 6.27246C13.3328 6.27246 14.3503 6.69389 15.1004 7.44403C15.8505 8.19418 16.272 9.2116 16.272 10.2725ZM14.272 10.2725C14.272 10.8029 14.0613 11.3116 13.6862 11.6867C13.3111 12.0617 12.8024 12.2725 12.272 12.2725C11.7415 12.2725 11.2328 12.0617 10.8578 11.6867C10.4827 11.3116 10.272 10.8029 10.272 10.2725C10.272 9.74203 10.4827 9.23332 10.8578 8.85825C11.2328 8.48317 11.7415 8.27246 12.272 8.27246C12.8024 8.27246 13.3111 8.48317 13.6862 8.85825C14.0613 9.23332 14.272 9.74203 14.272 10.2725Z"
              fill="#4D5163" />
            <path fill-rule="evenodd" clip-rule="evenodd"
              d="M5.794 16.5181C4.50484 15.2907 3.60861 13.7085 3.21864 11.9717C2.82867 10.2349 2.96248 8.42149 3.60315 6.76074C4.24381 5.1 5.36256 3.66652 6.81792 2.6416C8.27328 1.61668 9.99988 1.04634 11.7794 1.00271C13.5589 0.959071 15.3114 1.4441 16.8152 2.39646C18.3191 3.34882 19.5067 4.72574 20.228 6.35309C20.9493 7.98044 21.1718 9.78514 20.8675 11.539C20.5631 13.2928 19.7455 14.917 18.518 16.2061L12.312 22.7241L5.794 16.5181ZM17.07 14.8271L12.243 19.8971L7.173 15.0701C6.17037 14.1154 5.47334 12.8849 5.17005 11.5341C4.86676 10.1833 4.97084 8.77285 5.46912 7.48119C5.9674 6.18953 6.8375 5.07462 7.96941 4.27746C9.10131 3.4803 10.4442 3.03668 11.8282 3.00269C13.2122 2.96871 14.5752 3.3459 15.7449 4.08655C16.9146 4.8272 17.8383 5.89806 18.3994 7.16371C18.9605 8.42936 19.1336 9.83297 18.897 11.197C18.6604 12.5611 18.0246 13.8244 17.07 14.8271Z"
              fill="#4D5163" />
          </svg>
          Ver Mapa
        </button>
      </div>

      <dialog ref="csvExportDialog" class="csv-export-dialog" aria-labelledby="csv-export-title">
        <form @submit.prevent="exportCNPJCSV">
          <h3 id="csv-export-title">Exportar CSV</h3>
          <p>Escolha quantas páginas exportar, começando pela página 1 da pesquisa.</p>
          <label for="csv-export-pages">Quantidade de páginas</label>
          <input id="csv-export-pages" v-model.number="exportPageCount" type="number" min="1"
            :max="totalPages" step="1" required autofocus :disabled="exportingCSV" />
          <p>{{ totalPages }} páginas disponíveis, com até {{ itemsPerPage }} registros por página.</p>
          <p>A exportação continua enquanto você usa outras páginas da plataforma.</p>
          <p v-if="exportError" class="csv-export-error" role="alert">{{ exportError }}</p>
          <div class="csv-export-actions">
            <button type="button" class="small-button" @click="closeCSVExport" :disabled="exportingCSV">Cancelar</button>
            <button type="submit" class="small-button" :disabled="exportingCSV">
              {{ exportingCSV ? "Exportando..." : "Exportar" }}
            </button>
          </div>
        </form>
      </dialog>

      <!-- 🗺️ MAPA -->
      <div v-if="showMap" id="map" ref="map" class="cnpj-map"></div>
      <div v-if="showMap && routeInfo" class="route-info-box">
        <p><strong>🚗 Distância:</strong> {{ routeInfo.distance }}</p>
        <p><strong>⏱️ Duração:</strong> {{ routeInfo.duration }}</p>
        <button class="clear-route-button" @click="limparRota">
          🗑️ Limpar Rota
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/* global google */
import { getAuth } from "firebase/auth";
import { secureFetch } from "@/main.js";
import { csvExportState, startCSVExport, fetchCNPJPage as loadCNPJPage } from "@/services/cnpjCSVExport";

function storageKey(key, uid) {
  return `magleads:${uid}:${key}`;
}
async function getUid() {
  const auth = getAuth();
  const user = auth.currentUser;
  return user?.uid || "anonymous";
}

const apiBaseUrl =
  process.env.VUE_APP_API_BASE_URL || "https://177.36.4.221:3000";

const parseCnaes = (str) => {
  if (!str) return [];
  const arr = str
    .split(/[,\s;]+/)
    .map((v) => v.replace(/\D/g, ""))
    .filter(Boolean);
  return [...new Set(arr)];
};

export default {
  name: "CNPJSearch",
  data() {
    return {
      // 🔍 Dados de busca
      searchData: {
        codCNPJ: "",
        razSocial: "",
        //cnae: "",
        situacao: "",
        estado: "",
        municipio: "",
        bairro: "",
        cep: "",
        dataInicio: "",
        dataFim: "",
        capitalDe: "",
        capitalAte: "",
      },

      // 🔹 Definições dos campos (geram os inputs dinamicamente)
      fields: [
        { name: "codCNPJ", label: "CNPJ", type: "text", placeholder: "CNPJ" },
        {
          name: "razSocial",
          label: "Razão Social",
          type: "text",
          placeholder: "Razão Social",
        },
        //{ name: "cnae", label: "CNAE(s)", type: "text", placeholder: "Ex.: 4511101; 4520002; 4530701" },
        {
          name: "cnaesText",
          label: "CNAE(s)",
          type: "text",
          placeholder: "Ex.: 4511101; 4520002; 4530701",
        },
        {
          name: "situacao",
          label: "Situação Cadastral",
          type: "select",
          options: [
            { value: "", text: "Selecione..." },
            { value: "01", text: "Nula" },
            { value: "02", text: "Ativa" },
            { value: "03", text: "Suspensa" },
            { value: "04", text: "Inapta" },
            { value: "05", text: "Ativa Não Regular" },
            { value: "08", text: "Baixada" },
          ],
        },
        { name: "estado", label: "Estado (UF)", type: "select", options: [] },
        {
          name: "municipio",
          label: "Município",
          type: "text",
          placeholder: "Município",
        },
        {
          name: "bairro",
          label: "Bairro",
          type: "text",
          placeholder: "Bairro",
        },
        { name: "cep", label: "CEP", type: "text", placeholder: "CEP" },
        { name: "dataInicio", label: "Data de Abertura (De)", type: "date" },
        { name: "dataFim", label: "Data de Abertura (Até)", type: "date" },
        {
          name: "capitalDe",
          label: "Capital Social (De)",
          type: "money",
          placeholder: "R$ 0,00",
        },
        {
          name: "capitalAte",
          label: "Capital Social (Até)",
          type: "money",
          placeholder: "R$ 0,00",
        },
      ],

      // 🔹 Estado da página
      isWhatsConnected: localStorage.getItem("qrActive") === "true",
      cnpjResults: [],
      paginatedResults: [],
      ufs: [
        "AC",
        "AL",
        "AP",
        "AM",
        "BA",
        "CE",
        "DF",
        "ES",
        "GO",
        "MA",
        "MT",
        "MS",
        "MG",
        "PA",
        "PB",
        "PR",
        "PE",
        "PI",
        "RJ",
        "RN",
        "RS",
        "RO",
        "RR",
        "SC",
        "SP",
        "SE",
        "TO",
      ],
      currentPage: 1,
      itemsPerPage: 10,
      totalCount: 0,
      pageAmount: 0,
      loading: false,
      lastSearchRequest: null,
      exportPageCount: 1,
      exportError: "",
      map: null,
      markers: [],
      directionsService: null,
      directionsRenderer: null,
      userLocation: null,
      routeInfo: null,
      showMap: false,
    };
  },

  computed: {
    exportingCSV() {
      return csvExportState.status === "running";
    },
    totalPages() {
      return this.pageAmount;
    },
  },

  mounted() {
    // injeta opções de UF no schema dinâmico
    const ufField = this.fields.find((f) => f.name === "estado");
    if (ufField) {
      ufField.options = [{ value: "", text: "Selecione..." }].concat(
        this.ufs.map((uf) => ({ value: uf, text: uf }))
      );
    }
    this.debouncedBuscarCEP = this.debounce(this.buscarCEP, 500);
  },

  watch: {
    cnpjResults() {
      this.updatePagination();
      this.renderMapMarkers(); // mantém o mapa sincronizado com todos os resultados
    },
    "searchData.cep"(novoCep) {
      const cleanCep = String(novoCep || "").replace(/\D/g, "");

      if (cleanCep.length === 8) {
        this.debouncedBuscarCEP(cleanCep);
      }

      if (cleanCep.length !== 8) {
        this.searchData.estado = "";
        this.searchData.municipio = "";
        this.searchData.bairro = "";
      }
    }
  },

  methods: {
    parseCurrencyToNumber(s) {
      if (!s) return 0;
      const n =
        Number(
          String(s)
            .replace(/[^\d,-]/g, "")
            .replace(/\./g, "")
            .replace(",", ".")
        ) || 0;
      return n;
    },
    formatCNPJ(v) {
      const s = String(v || "")
        .replace(/\D/g, "")
        .padStart(14, "0")
        .slice(0, 14);
      return s.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*$/,
        "$1.$2.$3/$4-$5"
      );
    },
    formatCEP(v) {
      const s = String(v || "")
        .replace(/\D/g, "")
        .slice(0, 8);
      return s.replace(/^(\d{5})(\d{3}).*$/, "$1-$2");
    },
    formatPhoneBR(v) {
      const raw = String(v || "").replace(/\D/g, "");
      if (!raw) return "Não disponível";

      // remove DDI Brasil (55) quando presente
      let s = raw;
      if (s.startsWith("55") && s.length >= 12) s = s.slice(2);
      // remove zero tronco (ex.: 0 81 9xxxx-xxxx)
      if (s.startsWith("0") && s.length >= 11) s = s.slice(1);

      let ddd = "",
        local = s;
      if (s.length >= 10) {
        ddd = s.slice(0, 2);
        local = s.slice(2);
      }

      if (local.length === 9) {
        // celular
        return ddd
          ? `(${ddd}) ${local.slice(0, 5)}-${local.slice(5)}`
          : `${local.slice(0, 5)}-${local.slice(5)}`;
      }
      if (local.length === 8) {
        // fixo
        return ddd
          ? `(${ddd}) ${local.slice(0, 4)}-${local.slice(4)}`
          : `${local.slice(0, 4)}-${local.slice(4)}`;
      }
      // fallback se vier em formato inesperado
      return ddd ? `(${ddd}) ${local}` : v || "Não disponível";
    },
    formatMoneyBR(n) {
      const num =
        typeof n === "number"
          ? n
          : Number(
            String(n || "")
              .replace(/\./g, "")
              .replace(",", ".")
          );
      if (isNaN(num)) return n ?? "";
      return num.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    onMoneyFocus(field) {
      if (!this.searchData[field]) this.searchData[field] = "R$ 0,00";
    },
    onMoneyInput(field, e) {
      // formata enquanto digita
      const raw = String(e.target.value || "").replace(/[^\d]/g, "");
      const asNumber = (Number(raw) / 100).toFixed(2);
      this.searchData[field] = Number(asNumber).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    onMoneyBlur(field) {
      // garante consistência
      if (this.parseCurrencyToNumber(this.searchData[field]) === 0) {
        this.searchData[field] = ""; // deixa vazio quando zero
      }
    },
    formatarPorte(codigo) {
      if (!codigo) return "Não informado";
      const raw = String(codigo).trim();
      // normaliza: cobre "01", "1", "1.0"
      const norm = raw.includes(".")
        ? raw.split(".")[0].padStart(2, "0")
        : raw.padStart(2, "0");
      const mapa = {
        "01": "Microempresa",
        "03": "Empresa de Pequeno Porte",
        "05": "Demais",
      };
      return mapa[norm] || raw; // se não mapear, mostra o valor original
    },
    toggleMap() {
      this.showMap = !this.showMap;

      if (this.showMap) {
        // Garante que o <div ref="map"> já foi renderizado
        this.$nextTick(() => {
          if (!this.map) {
            this.loadGoogleMapsAPI(); // primeira vez
          } else {
            this.renderMapMarkers();  // reusa o mapa já criado
          }
        });
      }
    },
    async buscarCEP(cep) {
      const cleanCep = String(cep || "").replace(/\D/g, "");

      if (cleanCep.length !== 8) return;

      try {
        const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
        const data = await res.json();

        if (data.erro) {
          console.warn("CEP não encontrado");
          return;
        }

        if (!this.searchData.estado) {
          this.searchData.estado = data.uf || "";
        }

        if (!this.searchData.municipio) {
          this.searchData.municipio = data.localidade || "";
        }

        if (!this.searchData.bairro) {
          this.searchData.bairro = data.bairro || "";
        }

      } catch (err) {
        console.error("Erro ao buscar CEP:", err);
      }
    },
    debounce(fn, delay) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn(...args), delay);
      };
    },
    async prepareCNPJForSending() {
      // Normaliza para o padrão que Mensagens espera: 55 + DDD + número (somente dígitos)
      const to55 = (raw) => {
        let s = String(raw || "").replace(/\D/g, "");
        if (!s) return null;

        // remove 0800
        if (s.startsWith("0800")) return null;

        // remove tronco 0 se existir (ex.: 0819xxxx -> 819xxxx)
        if (s.startsWith("0") && s.length >= 11) s = s.slice(1);

        // se já tem 55, ok
        if (s.startsWith("55")) {
          // valida tamanho mínimo: 55 + 10/11
          if (s.length < 12) return null;
          return s;
        }

        // se NÃO tem 55, adiciona
        // aqui assumimos que já vem com DDD (10 ou 11 dígitos)
        if (s.length >= 10) return `55${s}`;

        return null;
      };

      const contactsToSend = (this.cnpjResults || [])
        .map((empresa) => {
          const number55 = to55(empresa.TELEFONE_1);
          if (!number55) return null;

          return {
            name: (empresa.RAZAO_SOCIAL || "Sem nome").trim(),
            number: number55, // ✅ agora sempre 55+DDD+numero
          };
        })
        .filter(Boolean);

      if (!contactsToSend.length) {
        alert("Nenhum contato válido encontrado para envio!");
        return;
      }

      // remove duplicados por número
      const uniqueContacts = Array.from(
        new Map(contactsToSend.map((c) => [c.number, c])).values()
      );

      // ✅ persiste por usuário
      const uid = await getUid();
      localStorage.setItem(
        storageKey("preparedContacts", uid),
        JSON.stringify(uniqueContacts)
      );

      // (opcional) fallback legado se tiver telas antigas lendo sem uid
      localStorage.setItem("preparedContacts", JSON.stringify(uniqueContacts));

      console.log("📦 preparedContacts salvo:", uniqueContacts.length);

      // ✅ vai para a tela de Mensagens
      this.$router.push("/enviar-mensagens");
    },
    openCSVExport() {
      if (this.loading || this.exportingCSV || !this.cnpjResults.length || !this.lastSearchRequest) return;
      this.exportPageCount = 1;
      this.exportError = "";
      this.$refs.csvExportDialog.showModal();
    },

    closeCSVExport() {
      if (!this.exportingCSV) this.$refs.csvExportDialog.close();
    },

    fetchCNPJPage(request, page) {
      return loadCNPJPage(request, page, secureFetch);
    },

    exportCNPJCSV() {
      if (this.exportingCSV) return;
      const pageCount = Number(this.exportPageCount);
      if (!Number.isInteger(pageCount) || pageCount < 1 || pageCount > this.totalPages) {
        this.exportError = `Informe uma quantidade inteira entre 1 e ${this.totalPages}.`;
        return;
      }
      if (!this.lastSearchRequest || !this.cnpjResults.length) {
        this.exportError = "Nenhum dado para exportar.";
        return;
      }
      this.exportError = "";
      this.$refs.csvExportDialog.close();
      void startCSVExport({
        request: this.lastSearchRequest,
        pageCount,
        totalPages: this.totalPages,
        secureFetch,
      });
    },

    formatDate(dateStr) {
      if (!dateStr || dateStr.length !== 8) return dateStr;
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      return `${day}/${month}/${year}`;
    },

    updatePagination() {
      this.paginatedResults = this.cnpjResults;
    },

    async previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;

        await this.searchCNPJ();
      }
    },

    async nextPage() {
      if (this.currentPage < this.pageAmount) {
        this.currentPage++;

        await this.searchCNPJ();
      }
    },

    // 🔍 PESQUISA PRINCIPAL
    async searchCNPJ() {
      console.log("Pesquisando CNPJ com os dados:", this.searchData);
      this.loading = true;

      try {
        // 🔹 Cria uma cópia dos dados e normaliza o formato esperado pelo backend
        const cleanedData = { ...this.searchData };

        if (cleanedData.razSocial) {
          cleanedData.razSocial = cleanedData.razSocial.toUpperCase().trim();
        }
        if (cleanedData.estado) {
          cleanedData.estado = cleanedData.estado.toUpperCase().trim();
        }
        if (cleanedData.municipio) {
          cleanedData.municipio = cleanedData.municipio.toUpperCase().trim();
        }
        if (cleanedData.bairro) {
          cleanedData.bairro = cleanedData.bairro.toUpperCase().trim();
        }
        if (cleanedData.codCNPJ)
          cleanedData.codCNPJ = cleanedData.codCNPJ.replace(/\D/g, "");
        if (cleanedData.cep)
          cleanedData.cep = cleanedData.cep.replace(/\D/g, "");

        cleanedData.cnaes = parseCnaes(cleanedData.cnaesText);
        delete cleanedData.cnaesText;

        /*if (cleanedData.cnae) {
          cleanedData.cnae = cleanedData.cnae.replace(/\D/g, ""); // só números
        }*/
        // parseia e remove zeros para não aplicar filtro acidental
        const capDe = this.parseCurrencyToNumber(cleanedData.capitalDe);
        const capAte = this.parseCurrencyToNumber(cleanedData.capitalAte);
        cleanedData.capitalDe = capDe > 0 ? capDe : "";
        cleanedData.capitalAte = capAte > 0 ? capAte : "";

        // opcional: se usuário inverteu os valores, normaliza
        if (
          cleanedData.capitalDe &&
          cleanedData.capitalAte &&
          cleanedData.capitalDe > cleanedData.capitalAte
        ) {
          const tmp = cleanedData.capitalDe;
          cleanedData.capitalDe = cleanedData.capitalAte;
          cleanedData.capitalAte = tmp;
        }

        // 🔹 Faz a requisição (token vai por dentro do secureFetch)
        const request = { ...cleanedData, size: this.itemsPerPage };
        const data = await this.fetchCNPJPage(request, this.currentPage - 1);
        this.lastSearchRequest = request;
        console.log("📦 Resposta do backend:", data);

        this.cnpjResults = Array.isArray(data.elements)
          ? data.elements
          : [];

        this.totalCount = data.totalCount || 0;
        this.pageAmount = data.pageAmount || 0;
        //this.loadGoogleMapsAPI();
        if (this.showMap) this.loadGoogleMapsAPI();
      } catch (error) {
        console.error("❌ Erro ao buscar CNPJs:", error);
      } finally {
        this.loading = false;
      }
    },

    // 🔹 Carrega a API do Google Maps
    async loadGoogleMapsAPI() {
      // Se a API já foi carregada em outra tela (MapsSearch, por exemplo)
      if (window.google && window.google.maps) {
        this.initMap();
        return;
      }

      try {
        const response = await secureFetch(`${apiBaseUrl}/get-google-api-key`, {
          method: "GET",
        });

        const data = await response.json();
        const apiKey = data.apiKey;

        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry`;
        script.async = true;
        script.defer = true;
        script.onload = () => this.initMap();
        document.head.appendChild(script);
      } catch (error) {
        console.error("❌ Erro ao carregar Google Maps API:", error);
      }
    },

    // 🔹 Inicializa o mapa
    async initMap() {
      // garante que o elemento do mapa existe
      if (!this.$refs.map) {
        this.$nextTick(() => this.initMap());
        return;
      }

      // tenta obter localização do usuário em paralelo (fallback)
      if (navigator.geolocation && !this.userLocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            this.userLocation = {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
            };
            console.log("📍 Localização do usuário:", this.userLocation);
          },
          () => console.warn("⚠️ Permissão de geolocalização negada.")
        );
      }

      // 👉 centro dinâmico baseado em CEP / município / UF / userLocation / Recife
      const center = await this.computeInitialCenter();

      this.map = new google.maps.Map(this.$refs.map, {
        center,
        zoom: 11,
      });

      // 🔹 Inicializa serviços de rota
      this.directionsService = new google.maps.DirectionsService();
      this.directionsRenderer = new google.maps.DirectionsRenderer({
        map: this.map,
      });

      // desenha os marcadores da pesquisa atual (se houver)
      this.renderMapMarkers();

      if (!google.maps.places) {
        console.warn("⏳ Aguardando API Places...");
        setTimeout(() => this.renderMapMarkers(), 50);
      } else {
        this.renderMapMarkers();
      }
    },

    // 🔹 Decide o centro inicial do mapa (CEP > Município/UF > geoloc > Recife)
    async computeInitialCenter() {
      const defaultCenter = { lat: -8.05428, lng: -34.8813 }; // Recife
      if (!window.google || !google.maps) {
        return defaultCenter;
      }

      const geocoder = new google.maps.Geocoder();

      const geocodeAddress = (address) =>
        new Promise((resolve) => {
          if (!address) return resolve(null);
          geocoder.geocode({ address }, (results, status) => {
            if (status === "OK" && results && results[0]) {
              resolve(results[0].geometry.location);
            } else {
              resolve(null);
            }
          });
        });

      try {
        // 1️⃣ CEP
        const rawCep = (this.searchData.cep || "").replace(/\D/g, "");
        if (rawCep.length === 8) {
          const loc = await geocodeAddress(`${rawCep}, Brasil`);
          if (loc) {
            console.log("🗺️ Centro definido pelo CEP:", rawCep);
            return loc;
          }
        }

        // 2️⃣ Município + UF
        const municipio = (this.searchData.municipio || "").trim();
        const uf = (this.searchData.estado || "").trim();

        if (municipio && uf) {
          const loc = await geocodeAddress(`${municipio}, ${uf}, Brasil`);
          if (loc) {
            console.log("🗺️ Centro definido por Município+UF:", municipio, uf);
            return loc;
          }
        }

        // 3️⃣ Só Município
        if (municipio && !uf) {
          const loc = await geocodeAddress(`${municipio}, Brasil`);
          if (loc) {
            console.log("🗺️ Centro definido por Município:", municipio);
            return loc;
          }
        }

        // 4️⃣ Só UF
        if (!municipio && uf) {
          const loc = await geocodeAddress(`${uf}, Brasil`);
          if (loc) {
            console.log("🗺️ Centro definido por UF:", uf);
            return loc;
          }
        }

        // 5️⃣ Localização do usuário (se já carregada)
        if (this.userLocation) {
          console.log("🗺️ Centro definido pela localização do usuário.");
          return new google.maps.LatLng(
            this.userLocation.lat,
            this.userLocation.lng
          );
        }
      } catch (err) {
        console.warn("⚠️ Erro ao calcular centro inicial do mapa:", err);
      }

      // 6️⃣ Fallback Recife
      console.log("🗺️ Centro fallback: Recife.");
      return defaultCenter;
    },

    limparRota() {
      if (this.directionsRenderer) {
        this.directionsRenderer.setDirections({ routes: [] });
      }
      this.routeInfo = null;
    },

    async tracarRota(destino) {
      if (!this.userLocation) {
        alert("Não foi possível obter sua localização atual.");
        return;
      }

      const request = {
        origin: this.userLocation,
        destination: destino,
        travelMode: google.maps.TravelMode.DRIVING,
      };

      this.directionsService.route(request, (result, status) => {
        if (status === "OK") {
          this.directionsRenderer.setDirections(result);
          const route = result.routes[0].legs[0];
          this.routeInfo = {
            distance: route.distance.text,
            duration: route.duration.text,
          };
        } else {
          this.routeInfo = null;
          alert("Não foi possível traçar a rota.");
        }
      });
    },

    // 🔹 Adiciona marcadores para todos os resultados
    async renderMapMarkers() {
      if (!this.map || !this.cnpjResults.length) return;

      // 🔹 Garante que a API Places foi carregada antes de continuar
      if (!window.google || !google.maps || !google.maps.places) {
        console.warn(
          "⚠️ Google Places ainda não carregado. Tentando novamente..."
        );
        setTimeout(() => this.renderMapMarkers(), 1000);
        return;
      }

      // 🔹 Agora é seguro criar o serviço
      const geocoder = new google.maps.Geocoder();
      const bounds = new google.maps.LatLngBounds();
      const infoWindow = new google.maps.InfoWindow();
      const service = new google.maps.places.PlacesService(this.map);

      // 🧹 Limpa marcadores antigos
      this.markers.forEach((m) => m.setMap(null));
      this.markers = [];

      for (const [index, empresa] of this.cnpjResults.entries()) {
        const endereco = `${empresa.ENDERECO}, ${empresa.BAIRRO}, ${empresa.Nome_Municipio}, ${empresa.UF}, ${empresa.CEP}`;

        try {
          await new Promise((resolve) => setTimeout(resolve, 50)); // Evita limite de requisições

          const result = await geocoder.geocode({ address: endereco });

          if (result.results.length) {
            const location = result.results[0].geometry.location;

            const marker = new google.maps.Marker({
              map: this.map,
              position: location,
              title: `${empresa.RAZAO_SOCIAL} — clique para traçar rota`,
            });

            // 🖼️ Busca foto do local (quando disponível)
            const placeRequest = {
              query: `${empresa.RAZAO_SOCIAL} ${empresa.ENDERECO} ${empresa.Nome_Municipio}`,
              fields: ["photos"],
            };

            let photoUrl = null;
            await new Promise((resolve) => {
              service.findPlaceFromQuery(placeRequest, (results, status) => {
                if (
                  status === google.maps.places.PlacesServiceStatus.OK &&
                  results[0]?.photos?.length
                ) {
                  photoUrl = results[0].photos[0].getUrl({
                    maxWidth: 200,
                    maxHeight: 150,
                  });
                }
                resolve();
              });
            });

            const infoContent = `
              <div style="font-family: Arial; max-width: 220px;">
                <h4 style="margin: 0; font-size: 12px;">${empresa.RAZAO_SOCIAL
              }</h4>
                <p style="font-size: 10px; margin: 2px 0;">
                  <strong>Endereço:</strong> ${empresa.ENDERECO || "Não disponível"
              }
                </p>
                <p style="font-size: 10px; margin: 2px 0;">
                  <strong>Telefone:</strong> ${empresa.TELEFONE_1 || "Não disponível"
              }
                </p>
                <p style="font-size: 10px; margin: 2px 0;">
                  <strong>Cidade:</strong> ${empresa.Nome_Municipio || "N/A"
              } - ${empresa.UF || ""}
                </p>
                ${photoUrl
                ? `<img src="${photoUrl}" alt="Imagem do local"
                        style="width: 100%; max-height: 100px; border-radius: 4px; margin-top: 4px;">`
                : ""
              }
                <p style="font-size: 9px; margin-top: 6px; color: #4285F4; font-style: italic;">
                  🖱️ Clique no marcador para traçar rota até este local.
                </p>
              </div>
            `;

            marker.addListener("mouseover", () => {
              infoWindow.setOptions({ disableAutoPan: true });
              infoWindow.setContent(infoContent);
              infoWindow.open(this.map, marker);
            });

            marker.addListener("mouseout", () => infoWindow.close());

            marker.addListener("click", () => {
              console.log(`🗺️ Rota solicitada para ${empresa.RAZAO_SOCIAL}`);
              this.tracarRota(marker.getPosition());
            });

            this.markers.push(marker);
            bounds.extend(location);
          }
        } catch (error) {
          console.warn(
            `⚠️ Erro ao geocodificar (${index + 1}): ${endereco}`,
            error
          );
        }
      }

      if (this.markers.length) {
        this.map.fitBounds(bounds);
      }

      console.log(
        `📍 ${this.markers.length} empresas exibidas no mapa com hover e miniaturas.`
      );
    },
  },
};
</script>

<style src="@/assets/styles.css"></style>

<style scoped>
.csv-export-dialog {
  width: min(440px, calc(100% - 32px));
  padding: 24px;
  border: none;
  border-radius: 12px;
  color: #4d5163;
}

.csv-export-dialog::backdrop {
  background: rgba(0, 0, 0, 0.45);
}

.csv-export-dialog input {
  display: block;
  width: 100%;
  margin: 8px 0;
  padding: 8px;
}

.csv-export-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}

.csv-export-error {
  color: #b42318;
}
</style>
